import { LitElement, html } from 'lit';
import { property, state } from 'lit/decorators.js';
import './mariana-scroll-dots.js';
import './mariana-scroll-arrows.js';

/**
 * Base Card Carousel Component
 * Abstract base class providing horizontal scroll functionality with indicators
 * Extended by specific card implementations (recipes, coupons, etc.)
 */
export abstract class BaseCardCarousel<T> extends LitElement {
  @property({ type: Array })
  cards: T[] = [];

  @state()
  protected scrollIndex: number = 0;

  protected isScrolling: boolean = false;
  protected scrollTimeout?: ReturnType<typeof setTimeout>;
  private containerCache?: Element;

  /**
   * Abstract method - must be implemented by child classes
   * Defines the container CSS class name for the scrollable area
   */
  protected abstract getContainerClass(): string;

  /**
   * Abstract method - must be implemented by child classes
   * Defines the card CSS class name
   */
  protected abstract getCardClass(): string;

  /**
   * Abstract method - must be implemented by child classes
   * Renders individual card content
   */
  protected abstract renderCard(card: T, index: number): unknown;

  render() {
    const containerClass = this.getContainerClass();
    const wrapperClass = containerClass.replace('-scroll-container', '-wrapper');

    return html`
      <div class="${wrapperClass}">
        <div class="${containerClass}">
          ${this.cards.map((card, index) => this.renderCard(card, index))}
        </div>
        <mariana-scroll-arrows
          .totalCards=${this.cards.length}
          .currentIndex=${this.scrollIndex}
          @arrow-click=${this.handleArrowClick.bind(this)}
        ></mariana-scroll-arrows>
      </div>
      <mariana-scroll-dots
        .totalCards=${this.cards.length}
        .currentIndex=${this.scrollIndex}
        @dot-click=${this.handleDotClick.bind(this)}
      ></mariana-scroll-dots>
    `;
  }

  protected firstUpdated(): void {
    this.setupScrollListener();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
  }

  protected setupScrollListener(): void {
    const container = this.renderRoot.querySelector(`.${this.getContainerClass()}`);
    if (!container) return;

    this.containerCache = container;
    container.addEventListener('scroll', () => {
      if (this.isScrolling) return;

      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = globalThis.setTimeout(() => this.updateScrollIndex(), 50);
    });
  }

  protected updateScrollIndex(): void {
    const container = this.containerCache || this.renderRoot.querySelector(`.${this.getContainerClass()}`);
    if (!container) return;

    const cards = Array.from(container.querySelectorAll(`.${this.getCardClass()}`));
    if (cards.length === 0) return;

    const { left, width, right } = container.getBoundingClientRect();
    const center = left + width / 2;

    // Check boundaries first
    const firstRect = cards[0].getBoundingClientRect();
    if (firstRect.left >= left - 10) {
      this.scrollIndex = 0;
      return;
    }

    const lastRect = cards[cards.length - 1].getBoundingClientRect();
    if (lastRect.right <= right + 10) {
      this.scrollIndex = cards.length - 1;
      return;
    }

    // Find closest to center
    this.scrollIndex = cards.reduce((closest, card, index) => {
      const { left: cardLeft, width: cardWidth } = card.getBoundingClientRect();
      const cardCenter = cardLeft + cardWidth / 2;
      const distance = Math.abs(cardCenter - center);

      const prevCard = cards[closest];
      const { left: prevLeft, width: prevWidth } = prevCard.getBoundingClientRect();
      const prevCenter = prevLeft + prevWidth / 2;

      return distance < Math.abs(prevCenter - center) ? index : closest;
    }, 0);
  }

  protected handleDotClick(event: CustomEvent): void {
    const { index } = event.detail;
    this.scrollToCard(index);
  }

  protected handleArrowClick(event: CustomEvent): void {
    const { index } = event.detail;
    this.scrollToCard(index);
  }

  private scrollToCard(index: number): void {
    const container = this.containerCache || this.renderRoot.querySelector(`.${this.getContainerClass()}`);
    const card = container?.children[index] as HTMLElement;

    if (!card) return;

    this.scrollIndex = index;
    this.isScrolling = true;
    card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    setTimeout(() => { this.isScrolling = false; }, 400);
  }
}
